import { http } from '../http'
import type { DashboardSummary } from '@/types/dashboard'
import type { ClaimedPlayer } from '@/types/player'
import type {
  ChangePasswordPayload,
  NotificationSettingsPayload,
  Server,
  UpdateProfilePayload,
  User,
} from '@/types/user'

/** `GET /user/getUser` — profile plus subscriptions, groups and favourite servers. */
export function getUser() {
  return http.get<User>('/user/getUser').then((response) => response.data)
}

/**
 * `GET /user/dashboard` — the home summary: the character on `serverId` and the
 * selected group. One request on purpose: rebuilding the home from each page's
 * endpoints cost six calls against a 30-per-minute limit.
 */
export function getDashboard(serverId: number | null) {
  return http
    .get<DashboardSummary>('/user/dashboard', {
      params: serverId === null ? undefined : { server_id: serverId },
    })
    .then((response) => response.data)
}

/**
 * `PATCH /user/profile/{id?}`. Accepts name, lobby_name, timezone, language and
 * propic. Without an id it updates the caller; with one it requires
 * admin/editor rights (or being that same user).
 */
export function updateProfile(payload: UpdateProfilePayload, id?: number) {
  const url = id === undefined ? '/user/profile' : `/user/profile/${id}`
  return http.patch<User>(url, payload).then((response) => response.data)
}

/** Mirrors the backend's `mimes:` rule. SVG is excluded there too: it can carry script. */
export const PROPIC_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const

/** Mirrors the backend's `max:5120`. */
export const PROPIC_MAX_BYTES = 5 * 1024 * 1024

/**
 * `POST /user/propic` — multipart upload of the avatar image. The backend
 * re-encodes it and answers with the updated user.
 *
 * The explicit `multipart/form-data` header is not decoration. This axios
 * instance defaults to `application/json`, and axios' own `transformRequest`
 * converts a FormData body into JSON whenever it sees a JSON content type — the
 * file would silently never leave the browser and the backend would report a
 * missing field. Setting a multipart type here skips that branch; the adapter
 * then overwrites the header with one carrying the real boundary.
 */
export function uploadPropic(file: File, onProgress?: (percent: number) => void) {
  const form = new FormData()
  form.append('propic', file)

  return http
    .post<User>('/user/propic', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // 5 MB over a slow mobile connection does not fit in the 20s default.
      timeout: 120_000,
      onUploadProgress: onProgress
        ? (event) => {
            if (!event.total) return
            onProgress(Math.round((event.loaded * 100) / event.total))
          }
        : undefined,
    })
    .then((response) => response.data)
}

/** `DELETE /user/propic` — clears the avatar and removes the stored object. */
export function deletePropic() {
  return http.delete<User>('/user/propic').then((response) => response.data)
}

/**
 * `POST /user/change-password`.
 * `current_password` may be omitted only for social accounts that have never
 * set a password.
 */
export function changePassword(payload: ChangePasswordPayload) {
  return http.post<string>('/user/change-password', payload).then((response) => response.data)
}

/**
 * `POST /user/discord/link` — the Discord authorize URL to send the browser to
 * with a full-page navigation. Discord comes back through the backend, which
 * redirects to `/profile?discord_link=success|error&reason=...`.
 */
export function linkDiscord() {
  return http
    .post<{ authorize_url: string }>('/user/discord/link')
    .then((response) => response.data)
}

/** `PATCH /user/notification-settings`. */
export function updateNotificationSettings(payload: NotificationSettingsPayload) {
  return http.patch<User>('/user/notification-settings', payload).then((response) => response.data)
}

/** `GET /user/servers` — the user's favourite servers. */
export function getFavoriteServers() {
  return http.get<Server[]>('/user/servers').then((response) => response.data)
}

/** `POST /user/servers/{id}` — idempotent; returns the updated list. */
export function addFavoriteServer(serverId: number) {
  return http.post<Server[]>(`/user/servers/${serverId}`).then((response) => response.data)
}

/**
 * `DELETE /user/servers/{id}` — returns the updated list.
 *
 * Side effect: the backend also releases the player claimed on that server, in
 * the same transaction, but answers with the servers only. Callers must drop
 * that server's player from their own state — see the user store.
 */
export function removeFavoriteServer(serverId: number) {
  return http.delete<Server[]>(`/user/servers/${serverId}`).then((response) => response.data)
}

/**
 * `DELETE /user/servers/{id}/archive` — permanently removes a *concluded*
 * server from the archive: favourite, claimed player and the user's own
 * declared troops/smithy on that server, all in one shot. The backend rejects
 * it (`SERVER_NOT_CONCLUDED`) if the server isn't closed — this is only for
 * the archive page, never for an active favourite.
 */
export function removeArchivedServer(serverId: number) {
  return http
    .delete<Server[]>(`/user/servers/${serverId}/archive`)
    .then((response) => response.data)
}

/** `GET /user/players` — the characters the user claimed, at most one per server. */
export function getClaimedPlayers() {
  return http.get<ClaimedPlayer[]>('/user/players').then((response) => response.data)
}

/**
 * `POST /user/players/{id}` — claims a character.
 *
 * Idempotent, and it enforces one player per server: whatever the user had
 * claimed on the same world is released server-side. Returns the whole updated
 * list, so callers should replace their state rather than patch it.
 */
export function claimPlayer(playerId: number) {
  return http.post<ClaimedPlayer[]>(`/user/players/${playerId}`).then((response) => response.data)
}

/**
 * `DELETE /user/players/{id}` — idempotent; returns the updated list.
 *
 * ⚠️ Dissociare un personaggio **cancella** le truppe e la fucina che l'utente
 * ha dichiarato per lui. Se ce ne sono, il backend rifiuta la prima chiamata con
 * `PLAYER_HAS_DECLARATIONS` e il numero di villaggi dichiarati: la conferma la
 * impone il server, non questo client, così nessuna interfaccia può cancellare
 * quel lavoro in silenzio. `confirm: true` procede.
 */
export function releasePlayer(playerId: number, confirm = false) {
  return http
    .delete<ClaimedPlayer[]>(`/user/players/${playerId}`, {
      data: confirm ? { confirm: true } : undefined,
    })
    .then((response) => response.data)
}
